import { useQuery, useMutation, useQueryClient, useInfiniteQuery, UseQueryOptions } from '@tanstack/react-query';
import api from '../services/api';
import { queryKeys } from '../lib/queryClient';
import { Client, CreateClientData, UpdateClientData, ClientFilters, PaginatedResponse } from '../types';

// Client API functions
const clientApi = {
  getClients: async (filters?: ClientFilters): Promise<PaginatedResponse<Client>> => {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.search) params.append('search', filters.search);
      if (filters.status) filters.status.forEach(s => params.append('status', s));
      if (filters.sort) params.append('sort', filters.sort);
      if (filters.order) params.append('order', filters.order);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
    }
    
    const response = await api.get(`/clients?${params}`);
    
    const responseData = response as any;
    const rawClientsData = responseData.data?.data?.items || responseData.data?.items || responseData.data || [];
    const clientsData = Array.isArray(rawClientsData) ? rawClientsData : [];
    const metaData = responseData.data?.data?.meta || responseData.data?.meta || {
      total: Array.isArray(clientsData) ? clientsData.length : 0,
      page: 1,
      limit: Array.isArray(clientsData) ? clientsData.length : 20,
      totalPages: 1
    };
    
    // Transform backend response to match frontend expectations
    return {
      data: clientsData,
      total: metaData.total || 0,
      page: metaData.page || 1,
      limit: metaData.limit || 20,
      totalPages: metaData.totalPages || 0,
      hasNext: (metaData.page || 1) < (metaData.totalPages || 0),
      hasPrev: (metaData.page || 1) > 1,
    };
  },
  
  getClient: async (id: string): Promise<Client> => {
    const response = await api.get(`/clients/${id}`);
    
    const responseData = response as any;
    
    if (!responseData || !responseData.data) {
      throw new Error('Client not found or invalid response structure');
    }
    
    return responseData.data;
  },
  
  createClient: async (clientData: CreateClientData): Promise<Client> => {
    const response = await api.post('/clients', clientData);
    const responseData = response as any;
    return responseData.data;
  },
  
  updateClient: async ({ id, ...updateData }: UpdateClientData & { id: string }): Promise<Client> => {
    const response = await api.put(`/clients/${id}`, updateData);
    const responseData = response as any;
    return responseData.data;
  },
  
  deleteClient: async (id: string): Promise<void> => {
    await api.delete(`/clients/${id}`);
  },
  
  getSpaceDistribution: async (): Promise<any[]> => {
    const response = await api.get('/clients/space-distribution');
    const responseData = response as any;
    return responseData.data;
  },
};

// Custom hooks
export const useClients = (
  filters?: ClientFilters, 
  options?: Partial<UseQueryOptions<PaginatedResponse<Client>>>
) => {
  return useQuery({
    queryKey: queryKeys.clients.list(filters),
    queryFn: () => clientApi.getClients(filters),
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false, // Disable auto-refetch for better performance
    ...options, // Merge any additional options
  });
};

// Infinite query for clients (useful for pagination)
export const useInfiniteClients = (filters?: Omit<ClientFilters, 'page'>) => {
  return useInfiniteQuery({
    queryKey: queryKeys.clients.list(filters),
    queryFn: ({ pageParam = 1 }) => clientApi.getClients({ ...filters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.hasNext ? lastPage.page + 1 : undefined;
    },
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });
};

export const useClient = (id: string) => {
  return useQuery({
    queryKey: queryKeys.clients.detail(id),
    queryFn: async () => {
      if (!id) {
        return null;
      }
      
      try {
        const result = await clientApi.getClient(id);
        
        // Ensure we always return something (not undefined)
        if (!result) {
          return null;
        }
        
        return result;
      } catch (error) {
        console.error('useClient queryFn error:', error);
        // Return null instead of throwing to prevent undefined
        return null;
      }
    },
    enabled: !!id, // Only run query if ID exists
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
    throwOnError: false, // Don't throw errors, handle them gracefully
  });
};

export const useCreateClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: clientApi.createClient,
    onSuccess: (newClient) => {
      // Invalidate clients list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all() });
      
      // Invalidate dashboard data as it might be affected
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.overview() });
      
      // Optimistically update the cache
      queryClient.setQueryData(queryKeys.clients.detail(newClient.id.toString()), newClient);
    },
  });
};

export const useUpdateClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: clientApi.updateClient,
    onSuccess: (updatedClient) => {
      // Update the specific client in cache
      queryClient.setQueryData(queryKeys.clients.detail(updatedClient.id.toString()), updatedClient);
      
      // Invalidate clients list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all() });
      
      // Invalidate dashboard data
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.overview() });
    },
  });
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: clientApi.deleteClient,
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.clients.detail(deletedId) });
      
      // Invalidate clients list
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all() });
      
      // Invalidate dashboard data
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.overview() });
    },
  });
};

export const useClientSpaceDistribution = () => {
  return useQuery({
    queryKey: queryKeys.clients.spaceDistribution(),
    queryFn: clientApi.getSpaceDistribution,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });
};